import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import * as moment from 'moment';
import { BehaviorSubject, Observable, merge, of } from 'rxjs';
import { delay, map, switchMap } from 'rxjs/operators';
import { IChartDatasets, IChartViewModel } from 'src/app/share/components/card-chart/card-chart.component';
import { FormInputComponent, IFormInputViewModel } from 'src/app/share/forms/form-input/form-input.component';
import { FormSelectComponent, IFormSelectChangedArg, IFormSelectViewModel } from 'src/app/share/forms/form-select/form-select.component';
import { IPageDataViewModel } from 'src/app/share/pages/pages';
import { CommonService } from 'src/app/share/services/common.service';
import { StringUtil } from 'src/app/share/utils/string-util';
import { ReportService } from '../services/report.service';
import { DateUtil } from 'src/app/share/utils/date-util';

@Component({
  selector: 'app-salesman-dashboard',
  templateUrl: './salesman-dashboard.component.html',
  styleUrls: ['./salesman-dashboard.component.scss']
})
export class SalesmanDashboardComponent implements OnInit {

  constructor(private commonService: CommonService, private router: Router, private reportService: ReportService) { }

  vm$: Observable<IViewModel>;

  private pageCommandBs$: BehaviorSubject<string> = new BehaviorSubject<string>('initialize');

  reportComponentUrl: string = 'salesman-report/';

  ngOnInit(): void {

    var today = new Date();

    const loader$: Observable<IViewModel> = of({
      filterData:  {
        fromDate: moment(new Date(today.getFullYear(), 0, 1)).format('YYYY-MM-DD'),
        toDate: moment(DateUtil.getLastDayOfPreviousMonth(today)).format('YYYY-MM-DD'),
        completedOnly: false,
        readyForBilling: false,
        yearToCompare: today.getFullYear()-1
      }
    }).pipe(
      switchMap(ctx => this.loadDashboardVm$(ctx.filterData).pipe(
        map((rvm: IDashboardViewModel) => {
          return {
            ...ctx,
            dashboardVm: rvm
          };
        })
      )),
      map(ctx => { 
        const ret: IViewModel = {
          actionsAllowed: true, canExportExcel: false,
          filterVm: {
            data: ctx.filterData,
            fromDateInputVm: FormInputComponent.viewModel(ctx.filterData, 'fromDate',
                      { title: 'From Date', isRequired: false, type: 'date' }),
            toDateInputVm: FormInputComponent.viewModel(ctx.filterData, 'toDate',
                      { title: 'To Date', isRequired: false, type: 'date' }),
            yearToCompareVm: FormSelectComponent.viewModel(ctx.filterData, 'yearToCompare',
                      { title: 'Year to Compare', isRequired: false, type: 'select' }, this.reportService.setYearToCompare())
          },
          dashboardVm: ctx.dashboardVm
        }
        return ret;
      })
    );

    // commands
    const comand$: Observable<IViewModel> = this.pageCommandBs$.pipe(
      switchMap(cmdText => { 
        const ret: IViewModel = {
          actionsAllowed: false, canExportExcel: false, 
          filterVm: undefined,
          dashboardVm: {
            workingText: cmdText == 'initialize' ? 'Initializing...' : cmdText == 'refresh' ? 'Refreshing...' : 'Loading...',
            errorText: undefined
          }
        };

        if (cmdText == 'refresh')
          return merge(of(ret), loader$);
        else
          return of(ret);
      })
    );

    this.vm$ = merge(comand$, loader$);
  }

  /**
   * 
   */
  private loadDashboardVm$(filterData: IDashboardFilterData): Observable<IDashboardViewModel> {
    return of({}).pipe(
      switchMap(ctx => this.commonService.getData('PageCommand', 'salesman.report', 'page-load', filterData, '/api/SantaCruz', true)),
      map((v: IDashboardViewModel) => {
        if (v.workingText)
          return { ...v };
        const ret = { ...v, actionsAllowed: true };
        if (ret.data) {

            ret.lastDateTimeUpdated = new Date().toLocaleDateString();  
            
            ret.revenueBySalesmanVm = this.chartViewModelVm(ret.data.revenueBySalesman, false, 'revenue-by-salesman', 'Revenue by Salesman', 'currency', 'bar', true, true)
            ret.salesmanNewAccountsVm = this.chartViewModelVm(ret.data.salesmanNewAccounts, false, 'salesman-new-accounts', 'New Accounts Open in 12 Month', 'number', 'bar', false, true)
          }
        return ret;
      })
    );
  }

  /**
   * 
   */
  private chartViewModelVm(data: IViewData, formatNumber: boolean, type: string, title: string, numberType: string = 'currency',
    chartType: string = 'pie', legend: boolean = true, isDetailsAvailable: boolean = true): IChartViewModel {

    if (data == undefined || data?.dataList?.length == 0)
      return {};

    const chartOptions: any = {
      responsive: true,
      legend: {
        position: 'top'
      },
      scales: {
        yAxes: [
          {
            gridLines: {
              display: true,
              color: "rgba(0, 0, 0, 0.1)"
            },
            ticks: {
              userCallback: function (tick: any) {
                return numberType == 'currency' ? StringUtil.toCurrency(tick) : StringUtil.toNumber(tick);
              }
            }
          }
        ]
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            return numberType == 'currency' ? StringUtil.toCurrency(context.parsed.y) : StringUtil.toNumber(context.parsed.y);
          }
        }
      },
      plugins: {
        plugins: {
          tooltip: {
            callbacks: {
              label: function (context) {
                return numberType == 'currency' ?  StringUtil.toCurrency(context.parsed.y) : StringUtil.toNumber(context.parsed.y);
              }
            }
          }
        }
      },
    }

    var chartDatasets: IChartDatasets[] = [];
    var chartLabels: any[] = [];
    var chartColors: any[] = [];
    if (data?.dataList[0]?.datasets) {
      chartDatasets.push({ label: data?.dataList[0]?.label ?? '', data: data?.dataList[0]?.datasets?.map(d => formatNumber ? this.parseAndFormatNumber((d.dataset ?? '')) : d.dataset ?? '') });
      chartLabels.push( { data: data?.dataList[0]?.datasets?.map(d => d.label ?? '') });
      chartColors.push(
        {
          backgroundColor: data?.dataList[0]?.datasets.map(b => b.color.backgroundColor ?? ''),
          hoverBackgroundColor: data?.dataList[0]?.datasets.map(b => b.color.hoverBackgroundColor ?? ''),
          borderWidth: Math.max(...data?.dataList[0]?.datasets.map(b => b.color.borderWidth ?? 2))
        }
      );
    }
    if (data?.dataList[1]?.datasets) {
      chartDatasets.push({ label: data?.dataList[1]?.label ?? '', data: data?.dataList[1]?.datasets?.map(d => formatNumber ? this.parseAndFormatNumber((d.dataset ?? '')) : d.dataset ?? '') });
      chartColors.push(
        {
          backgroundColor: data?.dataList[1]?.datasets.map(b => b.color.backgroundColor ?? ''),
          hoverBackgroundColor: data?.dataList[1]?.datasets.map(b => b.color.hoverBackgroundColor ?? ''),
          borderWidth: Math.max(...data?.dataList[1]?.datasets.map(b => b.color.borderWidth ?? 2))
        }
      );
    }

    const ret: IChartViewModel = {
      label: data?.label ?? '',
      chartType: chartType,
      chartOptions: chartOptions,
      chartDatasets: chartDatasets,
      chartLabels: data?.dataList[0]?.datasets?.map(d => d.label ?? ''),
      chartColors: chartColors,
      title: title,
      legend: legend,
      clientRoute: this.reportComponentUrl + type,
      isDetailsAvailable: isDetailsAvailable,
      chartClick: this.chartVm_clicked,
      chartHover: this.chartVm_hovered
    };

    return ret;
  }

  parseAndFormatNumber(num: string): string {
    const temp = Number(num);
    if (isNaN(temp))
      return num;
    return temp.toFixed(2);
  }

  /**
   * 
   */
  refreshPage() {
    this.pageCommandBs$.next('refresh');
  }

  viewDetailsClicked(url: string, line: string): void {
    this.router.navigateByUrl(url + '/' + line);
  }

  chartVm_clicked(vm: IViewModel, cvm: IChartViewModel, event: any): void {
  }


  chartVm_hovered(vm: IViewModel, cvm: IChartViewModel, event: any): void {
  }

  /**
   * 
   * @param pvm 
   */
  async doSearch(pvm: IViewModel) {
    try {
      pvm.dashboardVm.workingText = 'Filtering...';
      pvm.dashboardVm.errorText = undefined;

      var result = await of({}).pipe(
        delay(500),
        switchMap(ctx => this.loadDashboardVm$(pvm.filterVm.data))
      ).toPromise();

      pvm.dashboardVm = result;

    } catch (err) {
      pvm.dashboardVm.errorText = err;
    } finally {
      pvm.dashboardVm.workingText = undefined;
    }
  }

  yearToCompareChanged(e: IFormSelectChangedArg, vm: IViewModel) {

  }
}

/**
 * 
 */
interface IViewModel extends IPageDataViewModel {
  dashboardVm?: IDashboardViewModel;

  actionsAllowed?: boolean;

  filterVm?: {
    data: IDashboardFilterData;
    fromDateInputVm?: IFormInputViewModel;
    toDateInputVm?: IFormInputViewModel;
    yearToCompareVm?: IFormSelectViewModel;
  }

  isValid?: boolean;
}

/**
 * 
 */
interface IDashboardFilterData {
  fromDate?: string;
  toDate?: string;
  completedOnly?: boolean;
  readyForBilling?: boolean;
  yearToCompare?: number;
}

interface IDashboardViewModel {
  data?: { 
    revenueBySalesman: IViewData, salesmanNewAccounts: IViewData
  };

  revenueBySalesmanVm?: IChartViewModel;
  salesmanNewAccountsVm?: IChartViewModel;

  lastDateTimeUpdated?: string;
  workingText?: string;
  errorText?: string;
}

/**
 * 
 */
interface IViewData {
  label?: string;
  dataList?: IChartDataSet[];
}

/**
 * 
 */
interface IChartDataSet {
  label?: string;
  datasets?: IDataModel[];
}

/**
 * 
 */
interface IDataModel {
  dataset?: string;
  label?: string;
  color?: IChartColor;
}

/**
 * 
 */
interface IChartColor {
  backgroundColor?: string;
  hoverBackgroundColor?: string;
  borderWidth?: number;
}
